import {
  TCreateCustomerUserReqDto,
  TCreateCustomerUserResDto,
  TCustomerUserActionResDto,
  TCustomerUserCountResDto,
  TDisableCustomerUsersReqDto,
  TDisableCustomerUsersResDto,
  TGetCustomerUserDetailResDto,
  TGetCustomerUserListReqDto,
  TGetCustomerUserListResDto,
  TImportCustomerUserRow,
  TUpdateCustomerUserReqDto,
  TUpdateCustomerUserResDto,
  TValidateImportUsersResDto,
} from "@/api/services/main/customer-user/dtos/customer-user.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

const BASE = "/users";

export const fetchCustomerUserListApi = async (params: TGetCustomerUserListReqDto): Promise<TGetCustomerUserListResDto> => {
  const { data } = await clientApi.get<TGetCustomerUserListResDto>(BASE, { params });
  return data;
};

export const fetchCustomerUserDetailApi = async (id: number | string): Promise<TGetCustomerUserDetailResDto> => {
  const { data } = await clientApi.get<TGetCustomerUserDetailResDto>(`${BASE}/${id}`);
  return data;
};

export const fetchCustomerUserCountApi = async (): Promise<TCustomerUserCountResDto> => {
  const { data } = await clientApi.get<TCustomerUserCountResDto>(`${BASE}/count`);
  return data;
};

export const createCustomerUserApi = async (payload: TCreateCustomerUserReqDto): Promise<TCreateCustomerUserResDto> => {
  const { data } = await clientApi.post<TCreateCustomerUserResDto>(BASE, payload);
  return data;
};

export const updateCustomerUserApi = async (id: number | string, payload: TUpdateCustomerUserReqDto): Promise<TUpdateCustomerUserResDto> => {
  const { data } = await clientApi.put<TUpdateCustomerUserResDto>(`${BASE}/${id}`, payload);
  return data;
};

export const disableCustomerUsersApi = async (payload: TDisableCustomerUsersReqDto): Promise<TDisableCustomerUsersResDto> => {
  const { data } = await clientApi.post<TDisableCustomerUsersResDto>(`${BASE}/disable`, payload);
  return data;
};

export const resendCustomerUserInvitationApi = async (id: number | string): Promise<TCustomerUserActionResDto> => {
  const { data } = await clientApi.post<TCustomerUserActionResDto>(`${BASE}/${id}/resend-invitation`);
  return data;
};

export const resetCustomerUserPasswordApi = async (id: number | string): Promise<TCustomerUserActionResDto> => {
  const { data } = await clientApi.post<TCustomerUserActionResDto>(`${BASE}/${id}/password-reset`);
  return data;
};

export const validateImportCustomerUsersApi = async (rows: TImportCustomerUserRow[]): Promise<TValidateImportUsersResDto> => {
  const { data } = await clientApi.post<TValidateImportUsersResDto>(`${BASE}/import/validate`, { rows });
  return data;
};

export const useCustomerUserList = (params: TGetCustomerUserListReqDto) => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_LIST, params],
    queryFn: () => fetchCustomerUserListApi(params),
  });

  return { data: data?.data ?? [], pagination: data?.pagination, ...rest };
};

export const useCustomerUserDetail = (id: number | string | undefined) => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_DETAIL, id],
    queryFn: () => fetchCustomerUserDetailApi(id as number | string),
    enabled: id != null && id !== "",
  });

  return { data: data?.data, ...rest };
};

export const useCustomerUserCount = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_COUNT],
    queryFn: fetchCustomerUserCountApi,
  });

  return { count: data?.data?.count, ...rest };
};
